import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

const CURSOR_LOADER_OFFSET_PX = 12;

/**
 * @typedef {object} TableCellHoverPreviewState
 * @property {string | null} hoverUrl
 * @property {HTMLElement | null} element
 * @property {boolean} isPointerOnCell
 * @property {{ x: number, y: number } | null} cursorPosition
 */

/**
 * @typedef {object} TableCellHoverPreviewDispatchValue
 * @property {(hoverUrl: string, element: HTMLElement) => void} setHoveredPreview
 * @property {(event: React.MouseEvent) => void} updateCursorPosition
 * @property {() => void} leaveHoveredPreview
 * @property {() => void} clearHoveredPreview
 */

/** @type {React.Context<TableCellHoverPreviewState | undefined>} */
const TableCellHoverPreviewStateContext = createContext(undefined);

/** @type {React.Context<TableCellHoverPreviewDispatchValue | null>} */
const TableCellHoverPreviewDispatchContext = createContext(null);

const emptyDispatch = Object.freeze({
  /** @type {(hoverUrl: string, element: HTMLElement) => void} */
  setHoveredPreview: () => {},
  /** @type {(event: React.MouseEvent) => void} */
  updateCursorPosition: () => {},
  leaveHoveredPreview: () => {},
  clearHoveredPreview: () => {},
});

const idleState = Object.freeze({
  hoverUrl: null,
  element: null,
  isPointerOnCell: false,
  cursorPosition: null,
});

/**
 * Split hover-preview state from dispatch so cells can notify without re-rendering
 * when the overlay opens.
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 */
export function TableCellHoverPreviewProvider({ children }) {
  const [hoverState, setHoverState] = useState(
    /** @type {TableCellHoverPreviewState} */ (idleState),
  );

  const setHoveredPreview = useCallback((hoverUrl, element) => {
    setHoverState((prev) => ({
      hoverUrl,
      element,
      isPointerOnCell: true,
      cursorPosition: prev.cursorPosition,
    }));
  }, []);

  const updateCursorPosition = useCallback((/** @type {React.MouseEvent} */ event) => {
    setHoverState((prev) => ({
      ...prev,
      cursorPosition: {
        x: event.clientX + CURSOR_LOADER_OFFSET_PX,
        y: event.clientY + CURSOR_LOADER_OFFSET_PX,
      },
    }));
  }, []);

  const leaveHoveredPreview = useCallback(() => {
    setHoverState((prev) => (
      prev.isPointerOnCell
        ? { ...prev, isPointerOnCell: false }
        : prev
    ));
  }, []);

  const clearHoveredPreview = useCallback(() => {
    setHoverState(idleState);
  }, []);

  const dispatchValue = useMemo(
    () => ({
      setHoveredPreview,
      updateCursorPosition,
      leaveHoveredPreview,
      clearHoveredPreview,
    }),
    [setHoveredPreview, updateCursorPosition, leaveHoveredPreview, clearHoveredPreview],
  );

  return (
    <TableCellHoverPreviewDispatchContext.Provider value={dispatchValue}>
      <TableCellHoverPreviewStateContext.Provider value={hoverState}>
        {children}
      </TableCellHoverPreviewStateContext.Provider>
    </TableCellHoverPreviewDispatchContext.Provider>
  );
}

/**
 * @returns {TableCellHoverPreviewState}
 */
export function useTableCellHoverPreviewStateOptional() {
  const state = useContext(TableCellHoverPreviewStateContext);
  if (state === undefined) {
    return idleState;
  }
  return state;
}

/**
 * @returns {TableCellHoverPreviewDispatchValue}
 */
export function useTableCellHoverPreviewDispatchOptional() {
  const ctx = useContext(TableCellHoverPreviewDispatchContext);
  return ctx ?? emptyDispatch;
}
